import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  getParentMe, getParentStudentDemographics,
  getParentStudentAttendance, getParentStudentAssessments,
  updateParentContactInfo,
} from "../../lib/api";
import PortalCalendar from "./PortalCalendar";
import { PortalChangePassword } from "./PortalChangePassword";

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const CODE_STYLE = {
  P: "bg-emerald-100 text-emerald-800", PT: "bg-emerald-100 text-emerald-800",
  T: "bg-amber-100 text-amber-800", A: "bg-red-100 text-red-700",
  UA: "bg-red-100 text-red-700", EA: "bg-blue-100 text-blue-700",
};
const codeStyle = (c) => CODE_STYLE[c] || "bg-slate-100 text-slate-400";

const CAT_LABELS = {
  participation_level: "Participation", engagement: "Engagement", communication: "Communication",
  behavior: "Behavior", attention: "Attention", work_completion: "Work Completion",
};

function Field({ label, value }) {
  return (
    <div className="rounded-md bg-slate-50 border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium text-sm mt-0.5">{value || "—"}</p>
    </div>
  );
}

function OverviewTab({ studentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    getParentStudentDemographics(studentId)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, [studentId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Card><CardContent className="py-16 text-center text-muted-foreground">Loading…</CardContent></Card>;
  if (!data) return null;
  const mailing = data.mailing_street
    ? [data.mailing_street, [data.mailing_city, data.mailing_state, data.mailing_zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")
    : (data.mailing_address || "");

  return (
    <div className="space-y-4">
      <Card data-testid="parent-overview">
        <CardHeader><CardTitle className="text-lg">Student Information</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="Name" value={data.name} />
          <Field label="Preferred Name" value={data.preferred_name} />
          <Field label="Student #" value={data.student_number} />
          <Field label="Grade" value={data.grade} />
          <Field label="Date of Birth" value={data.dob} />
          <Field label="Pronouns" value={data.pronouns} />
          <Field label="Gender" value={data.gender} />
          <Field label="Enrollment Status" value={data.status} />
          <Field label="Start Date" value={data.start_date} />
          <Field label="School District" value={data.school_district} />
          <Field label="Mailing Address" value={mailing} />
        </CardContent>
      </Card>
      <ContactInfoCard studentId={studentId} data={data} onSaved={load} />
    </div>
  );
}

const CONTACT_FIELDS = [
  { key: "first_contact_phone", label: "First Contact Phone" },
  { key: "second_contact_phone", label: "Second Contact Phone" },
  { key: "address", label: "Home Address" },
  { key: "mailing_street", label: "Mailing Street" },
  { key: "mailing_city", label: "Mailing City" },
  { key: "mailing_state", label: "Mailing State" },
  { key: "mailing_zip", label: "Mailing ZIP" },
  { key: "transport_address", label: "Transportation Address" },
  { key: "other_address", label: "Other Address" },
];

function ContactInfoCard({ studentId, data, onSaved }) {
  const [form, setForm] = useState(() => {
    const o = {};
    CONTACT_FIELDS.forEach((f) => { o[f.key] = data[f.key] || ""; });
    return o;
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const o = {};
    CONTACT_FIELDS.forEach((f) => { o[f.key] = data[f.key] || ""; });
    setForm(o);
  }, [data]);

  const dirty = CONTACT_FIELDS.some((f) => (form[f.key] || "") !== (data[f.key] || ""));

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateParentContactInfo(studentId, form);
      if (res.data?.updated) {
        toast.success("Contact information updated. The school has been notified.");
        onSaved?.();
      } else {
        toast.info(res.data?.message || "No changes to save.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update contact information");
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="parent-contact-info">
      <CardHeader>
        <CardTitle className="text-lg">Contact Information</CardTitle>
        <p className="text-xs text-muted-foreground">Update phone numbers and addresses. Saving notifies the school.</p>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {CONTACT_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-xs text-muted-foreground block mb-1">{f.label}</label>
            <Input
              value={form[f.key] || ""}
              onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
              data-testid={`parent-contact-${f.key}`}
            />
          </div>
        ))}
        <div className="sm:col-span-2 flex justify-end pt-1">
          <Button onClick={save} disabled={saving || !dirty} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="parent-contact-save">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AttendanceTab({ studentId }) {
  const today = new Date();
  const [start, setStart] = useState(isoDate(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [end, setEnd] = useState(isoDate(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getParentStudentAttendance(studentId, start, end);
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load attendance"); }
    finally { setLoading(false); }
  }, [studentId, start, end]);

  useEffect(() => { load(); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const row = (data?.students || [])[0];
  const dates = data?.dates || [];
  const sm = row?.summary || {};

  return (
    <div className="space-y-4" data-testid="parent-attendance">
      <Card>
        <CardContent className="py-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">From</label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-44" data-testid="parent-att-start" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">To</label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44" data-testid="parent-att-end" />
          </div>
          <Button onClick={load} disabled={loading} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="parent-att-run">
            {loading ? "Loading..." : "View"}
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">Loading attendance…</CardContent></Card>
      ) : !row ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="parent-att-empty">No attendance in this range.</CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-[#00305E]">{sm.attendance_rate == null ? '—' : `${sm.attendance_rate}%`}</p><p className="text-xs text-muted-foreground">Attendance Rate</p></CardContent></Card>
            <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-emerald-600">{sm.present ?? 0}</p><p className="text-xs text-muted-foreground">Present</p></CardContent></Card>
            <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-amber-600">{sm.tardy ?? 0}</p><p className="text-xs text-muted-foreground">Tardy</p></CardContent></Card>
            <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-red-600">{sm.absent ?? 0}</p><p className="text-xs text-muted-foreground">Absent</p></CardContent></Card>
          </div>
          <Card>
            <CardHeader><CardTitle className="text-base">Daily Record</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {dates.map((d) => {
                const cell = row.daily_attendance?.[d] || {};
                const code = cell.code === "-" ? "" : (cell.code || "");
                return (
                  <div key={d} className="text-center">
                    <div className={`w-10 h-8 flex items-center justify-center rounded text-xs font-semibold ${codeStyle(code)}`}>{code}</div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{d.slice(5)}</p>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function AssessmentsTab({ studentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    getParentStudentAssessments(studentId)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load assessments"))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <Card><CardContent className="py-16 text-center text-muted-foreground">Loading…</CardContent></Card>;
  const averages = data?.averages || {};
  const entries = data?.assessments || [];

  return (
    <div className="space-y-4" data-testid="parent-assessments">
      <Card>
        <CardHeader><CardTitle className="text-lg">Assessment Averages</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {(data?.scored_entries ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="parent-assess-empty">No assessment data recorded yet.</p>
          ) : Object.keys(CAT_LABELS).map((cat) => {
            const v = averages[cat];
            const pct = v ? (v / 4) * 100 : 0;
            return (
              <div key={cat}>
                <div className="flex justify-between text-sm mb-1"><span>{CAT_LABELS[cat]}</span><span className="font-semibold">{v ?? "—"}{v ? " / 4" : ""}</span></div>
                <div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-[#00305E]" style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground pt-2">{data?.scored_entries ?? 0} scored · {data?.dna_entries ?? 0} did-not-attend</p>
        </CardContent>
      </Card>
      {entries.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Recent Entries</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-slate-50 text-left"><th className="p-2">Date</th><th className="p-2">Block</th>{Object.values(CAT_LABELS).map((l) => <th key={l} className="p-2 text-center">{l.slice(0, 4)}</th>)}</tr></thead>
              <tbody>
                {entries.slice(0, 30).map((a) => (
                  <tr key={a.id} className="border-b">
                    <td className="p-2 whitespace-nowrap">{a.date}</td>
                    <td className="p-2 whitespace-nowrap">{a.block || a.class_name || "—"}</td>
                    {Object.keys(CAT_LABELS).map((c) => <td key={c} className="p-2 text-center">{a.did_not_attend ? "DNA" : (a[c] ?? "—")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function ParentPortal({ me: initialMe }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(initialMe);
  const [tab, setTab] = useState("overview");
  const [activeStudent, setActiveStudent] = useState(null);

  useEffect(() => {
    getParentMe().then((r) => {
      setMe(r.data);
      if (r.data.students?.length) setActiveStudent((prev) => prev || r.data.students[0].id);
    }).catch(() => {});
  }, []);

  const students = me?.students || [];
  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "attendance", label: "Attendance" },
    { key: "assessments", label: "Assessments" },
    { key: "calendar", label: "Calendar" },
    { key: "account", label: "My Account" },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="parent-portal">
      <header className="text-white" style={{ backgroundColor: "#00305E" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <img src="/logo_full.png" alt="SMARTIS" className="h-9 w-auto rounded bg-white/95 p-1" />
          <div className="min-w-0">
            <h1 className="text-lg font-bold leading-tight">Parent Portal</h1>
            <p className="text-xs text-white/70 truncate">{me?.name} · {me?.email}</p>
          </div>
          <div className="ml-auto">
            <button onClick={() => { logout(); navigate("/login"); }} className="text-sm bg-white/10 hover:bg-white/20 transition-colors rounded-md px-3 py-1.5" data-testid="parent-logout-btn">Sign Out</button>
          </div>
        </div>
        <nav className="max-w-5xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.key ? "border-white text-white" : "border-transparent text-white/60 hover:text-white/90"}`}
              data-testid={`parent-tab-${t.key}`}>{t.label}</button>
          ))}
        </nav>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {tab === "calendar" ? (
          <PortalCalendar />
        ) : tab === "account" ? (
          <PortalChangePassword />
        ) : students.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="parent-no-students">
            No students are currently linked to your account. Please contact the school administrator.
          </CardContent></Card>
        ) : (
          <>
            {students.length > 1 && (
              <div className="flex flex-wrap gap-2" data-testid="parent-student-switcher">
                {students.map((s) => (
                  <button key={s.id} onClick={() => setActiveStudent(s.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${activeStudent === s.id ? "bg-[#00305E] text-white border-[#00305E]" : "bg-white text-slate-700 border-slate-200 hover:border-[#00305E]"}`}
                    data-testid={`parent-student-${s.id}`}>{s.name}</button>
                ))}
              </div>
            )}
            {activeStudent && (
              <>
                {tab === "overview" && <OverviewTab studentId={activeStudent} />}
                {tab === "attendance" && <AttendanceTab studentId={activeStudent} />}
                {tab === "assessments" && <AssessmentsTab studentId={activeStudent} />}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
